'use client'
import { useState, useEffect, useRef } from 'react'

export default function KingSolarmanChatbot() {
    const [isOpen, setIsOpen] = useState(false)
    const [language, setLanguage] = useState(() => {
        // Get saved language preference or default to English
        const saved = localStorage.getItem('chatbotLanguage');
        return saved || 'en';
    });

    const [messages, setMessages] = useState([
        {
            id: 1,
            text: language === 'en' 
                ? "Hi! Welcome to KingSolarman. How can I help you with solar energy solutions today?"
                : "Dumela! O amogeleswa ke KingSolarman. Nka thusa jang ka didiriswa tsa letsatsi?",
            sender: 'bot',
            timestamp: new Date()
        }
    ])
    const [inputMessage, setInputMessage] = useState('')
    const [isTyping, setIsTyping] = useState(false)
    const [failedAnswers, setFailedAnswers] = useState(0)
    const [showEscalation, setShowEscalation] = useState(false)
    const messagesEndRef = useRef(null)
    const [showQuickActions, setShowQuickActions] = useState(true)
    
    // Typewriter effect state
    const [typingMessageId, setTypingMessageId] = useState(null)
    const [displayedText, setDisplayedText] = useState('')

    // Save language preference to localStorage
    useEffect(() => {
        localStorage.setItem('chatbotLanguage', language);
    }, [language]);

    // Translations object
    const translations = {
        en: {
            placeholder: "Type your message...",
            welcome: "Hi! Welcome to KingSolarman. How can I help you with solar energy solutions today?",
            clearChat: "Clear chat",
            agentTyping: "Assistant is typing...",
            chatWithAgent: "Chat with an Agent",
            quickActions: "Quick actions to get started:",
            browseProducts: "Browse Products",
            installationServices: "Installation Services",
            solarFinancing: "Solar Financing",
            contactInfo: "Contact Info",
            online: "🟢 Online - Ready to chat!",
            needHuman: "I completely understand! 😊 Let me connect you with one of our human agents right away. They can provide you with personalized assistance for your solar energy inquiry.",
            perhapsChat: "Perhaps you can chat with our agent"
        },
        tn: {
            placeholder: "Ngwala molaetsa wena...",
            welcome: "Dumela! O amogeleswa ke KingSolarman. Nka thusa jang ka didiriswa tsa letsatsi?",
            clearChat: "Tlosa kgoo",
            agentTyping: "Mothusaši o ngwala...",
            chatWithAgent: "Buisa le Mothusaši",
            quickActions: "Ditheko tsa botlhano go thoma:",
            browseProducts: "Bontsha Dithoto",
            installationServices: "Ditirelo tsa Tuelo",
            solarFinancing: "Dithuso tsa Dituelo",
            contactInfo: "Info ya Dithaelano",
            online: "🟢 Ka gare - K готов go bua!",
            needHuman: "Keakanya thata! 😊 Ke tla o kopotšana le mothusaši wa rona ka bonako. O tla go thusa ka dipotso tsa gago tsa taolo ya enerji ya letsatsi.",
            perhapsChat: "Ka boikopanyo, buisa le mothusaši wa rona"
        }
    };

    const t = translations[language];




    // Knowledge base for the chatbot with comprehensive response options
    const knowledgeBase = {
        greetings: {
            patterns: ['hello', 'hi', 'hey', 'good morning', 'good afternoon', 'good evening', 'greetings', 'morning', 'afternoon'],
            responses: [
                "Hi! Welcome to KingSolarman. How can I help you with solar energy solutions today?",
                "Hello! Ready to harness Botswana's sunshine for your energy needs?",
                "Hey there! What solar questions can I help with?",
                "Hi! I'm here to help with solar panels, inverters, batteries, and installations.",
                "Good to see you! How can I assist with your solar energy needs?",
                "Welcome! Whether residential or commercial solar, I'm here to help.",
                "Hi there! Let me help you navigate our solar energy solutions.",
                "Hello! Ready to explore solar products or get installation advice?"
            ]
        },

        greetings_tn: {
            patterns: ['dumela', 'thobota', 'moro', 'mosong', 'rra', 'ndebele', 'hello', 'hi', 'hey'],
            responses: [
                "Dumela! O amogeleswa ke KingSolarman. Nka thusa jang ka didiriswa tsa letsatsi?",
                "Dumela! U na le maikaelelo a letsatsi a Botswana?",
                "Thobota! Ke molaetsa wa letsatsi, o batla thuso ya eng?",
                "Dumela! Ke tla go thusa ka diphanele tsa letsatsi, inverters, batiri, le tuelo.",
                "O kgatlhaneng! Nka go thusa jang ka tshwanelo ya enerji ya letsatsi?",
                "Amogeleswa! Le ntseng jang wa ga rona, ke tla go thusa.",
                "Dumela! Tla ke go thusa go lebelela didiriswa tsa letsatsi.",
                "Dumela! U tshwanelwa ke go lebelela ditšhwanelo tsa letsatsi?"
            ]
        },

        about: {
            patterns: ['about', 'what is kingsolarman', 'tell me about', 'kingsolarman', 'company', 'business', 'who are you', 'what do you do'],
            responses: [
                "KingSolarman is Botswana's leading solar energy provider since 2010. We specialize in solar panels, inverters, batteries, and installations.",
                "We're a local solar company making renewable energy accessible and affordable across Botswana.",
                "KingSolarman helps you transition to solar energy with quality products and professional installation services.",
                "Your trusted solar energy partner in Botswana for residential and commercial solutions.",
                "KingSolarman revolutionizes energy independence in Botswana with comprehensive solar solutions.",
                "We're Botswana's premier solar energy company, making clean energy seamless.",
                "KingSolarman - your one-stop solution for solar energy in Botswana.",
                "We simplify solar energy adoption, connecting customers with reliable renewable solutions."
            ]
        },

        about_tn: {
            patterns: ['ka ga rona', 're bontsho', 'mokopani', 'kgwebo', 'mongwe', 'loshong', 'who are you'],
            responses: [
                "KingSolarman ke mokapodi wa pele wa enerji ya letsatsi mo Botswana go tloga ka 2010. Re ikantšitše ka diphanele tsa letsatsi, inverters, batiri, le tuelo.",
                "Re komponi ya letsatsi ya selegae e dirang enerji e ntseng e tsenywa mo Botswana ka tshwanelo.",
                "KingSolarman o go thusa go fetela kwa letsatsini ka didiriswa tsa boleng le ditirelo tsa setso.",
                "Moakanyi wa gago wa enerji ya letsatsi mo Botswana bakeng sa residential le commercial.",
                "KingSolarman e fokotsaenerji mo Botswana ka didiriswa tsa letsatsi tsa tshwanelo.",
                "Re kompani ya pele ya enerji ya letsatsi mo Botswana.",
                "KingSolarman - tharollo ya gago ya nngwe ya enerji ya letsatsi mo Botswana.",
                "Re nolofatsa kabo ya enerji ya letsatsi, go amogelela bareki ka didiriswa tse di tiileng."
            ]
        },

        contact: {
            patterns: ['contact', 'phone', 'email', 'whatsapp', 'location', 'address', 'call', 'reach', 'communicate', 'get in touch'],
            responses: [
                "WhatsApp: +267 71548257 (fastest response!) | Visit us at International Commerce Park, Gaborone",
                "Contact us: WhatsApp +267 71548257 or visit our showroom in Gaborone",
                "Visit us: International Commerce Park, Gaborone, Botswana. WhatsApp: +267 71548257",
                "24/7 WhatsApp: +267 71548257 for solar consultations and support",
                "📞 WhatsApp: +267 71548257 | 📍 International Commerce Park, Gaborone",
                "Quick contact: WhatsApp +267 71548257 for instant solar advice!",
                "Main line: WhatsApp +267 71548257 | Office: International Commerce Park, Gaborone",
                "Located at International Commerce Park, Gaborone. WhatsApp: +267 71548257"
            ]
        },

        contact_tn: {
            patterns: ['dithaelano', 'mogala', 'imeile', 'whatsapp', 'seemo', 'aterese', 'lphetisa', 'fitshane'],
            responses: [
                "WhatsApp: +267 71548257 (karabo e e bonolo!) | Etela International Commerce Park, Gaborone",
                "Itsaea rona: WhatsApp +267 71548257 goba etela showroomo ya rona mo Gaborone",
                "Etela rona: International Commerce Park, Gaborone, Botswana. WhatsApp: +267 71548257",
                "WhatsApp: +267 71548257 ka dinako tsohle bakeng sa dikgakololo tsa letsatsi",
                "📞 WhatsApp: +267 71548257 | 📍 International Commerce Park, Gaborone",
                "Dithaelano tsa botlhano: WhatsApp +267 71548257!",
                "Molaetsa wa botlhano: WhatsApp +267 71548257 | Ofisi: International Commerce Park, Gaborone",
                "Mo: International Commerce Park, Gaborone. WhatsApp: +267 71548257"
            ]
        },

        products: {
            patterns: ['products', 'solar panels', 'inverters', 'batteries', 'what do you sell', 'inventory', 'catalog'],
            responses: [
                "We offer premium solar panels, hybrid inverters, lithium batteries, and complete solar system kits.",
                "Our products include Jinko solar panels, Deye inverters, Victron systems, and lithium battery solutions.",
                "Quality solar equipment from leading brands: panels, inverters, batteries, mounting systems, and accessories.",
                "From residential 5kW systems to commercial solar farms - we have the right solar solution.",
                "Solar products: panels (Jinko, Longi), inverters (Deye, Victron, Fronius), batteries (Lithium, AGM), and mounting systems.",
                "Complete solar energy solutions including installation services and after-sales support.",
                "We stock solar panels, hybrid inverters, battery storage, and all accessories for solar installations.",
                "Premium solar equipment with warranties and professional installation across Botswana."
            ]
        },

        products_tn: {
            patterns: ['dithoto', 'diphanele tsa letsatsi', 'inverters', 'batiri', 'dokomoko', 'catalog'],
            responses: [
                "Re bontsha diphanele tsa letsatsi tsa botlhano, inverters tsa hybrid, batiri tsa lithium, le meaho yotlwana ya letsatsi.",
                "Didiriswa tsa rona di akaretsa diphanele tsa Jinko, inverters tsa Deye, sisiteme tsa Victron, le ditšhwanelo tsa batiri.",
                "Didiriswa tsa letsatsi tsa boleng go tloga go mebala e e hantileng: diphanele, inverters, batiri, le tse dingwe.",
                "Go tloga ka meaho ya 5kW ya residential go ya ka dijobo tsa letsatsi - re na le tharollo ya letsatsi.",
                "Didiriswa tsa letsatsi: diphanele (Jinko, Longi), inverters (Deye, Victron, Fronius), batiri (Lithium, AGM).",
                "Tharollo e e tswang ya enerji ya letsatsi go akaretsa ditirelo tsa tuelo le morogo.",
                "Re na le diphanele tsa letsatsi, inverters tsa hybrid, boloko ya batiri, le didiriswa tsohle.",
                "Didiriswa tsa letsatsi tsa botlhano le ditirelo tsa setso mo Botswana."
            ]
        },

        installation: {
            patterns: ['installation', 'install', 'setup', 'mounting', 'how to install', 'installation service'],
            responses: [
                "We provide professional solar installation services across Botswana. WhatsApp +267 71548257 for a quote.",
                "Our certified teams install solar systems with minimal disruption to your home or business.",
                "Professional installation including panel mounting, inverter setup, and battery integration.",
                "Installation services cover residential, commercial, and industrial solar projects.",
                "We handle permits, installation, and grid connection - complete solar solution.",
                "Expert installation teams with years of experience in Botswana's solar projects.",
                "From rooftop to ground-mounted systems - professional installation guaranteed.",
                "Installation includes system design, equipment supply, and commissioning services."
            ]
        },

        installation_tn: {
            patterns: ['tuelo', 'tiragatsa', 'go kgetha', 'tuelo', 'tiragatsa jang'],
            responses: [
                "Re fana ka ditirelo tsa tuelo tsa setso mo Botswana. WhatsApp +267 71548257 bakeng sa kotsiso.",
                "Ditimakgolo tsa rona tse di netefaditsweng di diragatsa meaho ya letsatsi ka phokotso.",
                "Tuelo ya setso e akaretsa go kgetha diphanele, go seta inverter, le kgatelelo ya batiri.",
                "Ditirelo tsa tuelo di akaretsa residential, commercial, le dijobo tsa botshelo.",
                "Re laola ditumelwana, tuelo, le kgokagano ya grid - tharollo e e tswang.",
                "Ditimakgolo tsa tuelo tse eksperdi ka lemoga mo Botswana.",
                "Go tloga ka legong la marogo go ya ka moširi - tuelo ya setso e e netefaditsweng.",
                "Tuelo e akaretsa moralo wa sisiteme, thomelo ya didiriswa, le ditirelo tsa kholo."
            ]
        },

        whatsapp: {
            patterns: ['whatsapp', 'chat', 'message', 'contact', 'whats app'],
            responses: [
                "All communications go through WhatsApp. It's fast and convenient!",
                "Chat with our solar experts via WhatsApp for instant responses.",
                "WhatsApp makes communication quick and easy for solar consultations.",
                "Direct WhatsApp contact with our solar specialists - no delays!"
            ]
        },

        whatsapp_tn: {
            patterns: ['whatsapp', 'kgoo', 'molaetsa', 'dithaelano'],
            responses: [
                "Dithaelano tsohle di feta ka WhatsApp. Ke botlhano le go bonolo!",
                "Buisa le batsebi ba rona ba letsatsi ka WhatsApp bakeng sa karabo e e botlhano.",
                "WhatsApp e dirang dithaelano di be bo botlhano le go bonolo.",
                "Dithaelano tsa WhatsApp ka batsebi ba rona - ga di na goDelayed!"
            ]
        },

        location: {
            patterns: ['where', 'location', 'address', 'gaborone', 'botswana', 'find you', 'showroom'],
            responses: [
                "We're at International Commerce Park, Gaborone, Botswana.",
                "Visit us in Gaborone - we're at International Commerce Park for easy access.",
                "Main office: International Commerce Park, Gaborone, Botswana",
                "Gaborone based and serving all of Botswana with solar solutions!"
            ]
        },

        location_tn: {
            patterns: ['kae', 'seemo', 'aterese', 'gaborone', 'botswana', 'go kgona'],
            responses: [
                "Re mo International Commerce Park, Gaborone, Botswana.",
                "Etela rona mo Gaborone - re mo International Commerce Park.",
                "Ofisi e e kwa tlase: International Commerce Park, Gaborone, Botswana",
                "Re mo Gaborone re direla Botswana yotlwana ka tharollo tsa letsatsi!"
            ]
        },

        prices: {
            patterns: ['price', 'cost', 'expensive', 'cheap', 'budget', 'affordable', 'money', 'pricing'],
            responses: [
                "Solar system prices vary by size and requirements. WhatsApp +267 71548257 for a custom quote.",
                "No hidden fees - all solar pricing is upfront and competitive.",
                "Check our solar products for transparent pricing on panels, inverters, and batteries.",
                "Fair pricing with quality solar equipment and professional installation."
            ]
        },

        prices_tn: {
            patterns: ['theko', 'madi', 'thebogo', 'theko e e phethegileng', 'madi a mokotla'],
            responses: [
                "Ditheko tsa meaho ya letsatsi di fapana ka bophara le dinoko. WhatsApp +267 71548257 bakeng sa kotsiso.",
                "Ga go mafa a boleng - ditheko tsohle tsa letsatsi di bonolo le go kwejana.",
                "Leba didiriswa tsa rona tsa letsatsi bakeng sa ditheko tse bonolo.",
                "Ditheko tsa loalo le didiriswa tsa letsatsi tsa boleng le tuelo ya setso."
            ]
        },

        features: {
            patterns: ['features', 'what can you do', 'help', 'services', 'what do you offer'],
            responses: [
                "We provide solar panels, inverters, batteries, installation, and maintenance services.",
                "Browse solar products, get consultations, installation services, and technical support.",
                "Solar energy services: product sales, installation, maintenance, and consultations.",
                "Everything for solar energy: panels, inverters, batteries, installation, and support."
            ]
        },

        features_tn: {
            patterns: ['ditsheko', 'o ka dirang', 'thus', 'ditirelo', 'o bontsha'],
            responses: [
                "Re bontsha diphanele tsa letsatsi, inverters, batiri, tuelo, le ditirelo tsa tshireletso.",
                "Leba didiriswa tsa letsatsi, get dikgakololo, ditirelo tsa tuelo, le thuso ya thekniki.",
                "Ditirelo tsa enerji ya letsatsi: theko ya didiriswa, tuelo, tshireletso, le dikgakololo.",
                "Tsohle ya enerji ya letsatsi: diphanele, inverters, batiri, tuelo, le thuso."
            ]
        },

        financing: {
            patterns: ['finance', 'financing', 'loan', 'payment', 'installment', 'credit', 'payment plan'],
            responses: [
                "We can connect you with solar financing partners. WhatsApp +267 71548257 for details.",
                "Solar financing available through partners. Contact us to discuss options.",
                "Need solar financing? We work with financing partners. WhatsApp us to learn more.",
                "Payment plans available for solar systems. Ask about financing options."
            ]
        },

        financing_tn: {
            patterns: ['dituelo', 'ditloan', 'dithuso', 'peyamente', 'tshwanelo'],
            responses: [
                "Re ka o amogela le batho ba re thusang ka dituelo. WhatsApp +267 71548257 bakeng ga maina.",
                "Dithuso tsa dituelo di gona ka batho ba re šwanang nae. Itsaea rona go bua ka dikgetsi.",
                "O batla dithuso tsa dituelo? Re na le batho ba re dirisanang nae. WhatsApp re go bolele.",
                "Dikgato tsa dituelo di gona bakeng sa meaho ya letsatsi. Botsa ka dithuso tsa dituelo."
            ]
        },

        safety: {
            patterns: ['safe', 'safety', 'quality', 'warranty', 'legitimate', 'trust'],
            responses: [
                "All our solar products come with manufacturer warranties and local support.",
                "We use only certified solar equipment from reputable international brands.",
                "Stay confident: professional installation, quality products, and ongoing support.",
                "Safe solar solutions start with quality equipment and professional installation."
            ]
        },

        safety_tn: {
            patterns: ['tshireletso', 'boleng', 'tumelano', 'netefatso', 'tshwanelwa'],
            responses: [
                "Didiriswa tsa letsatsi tsohle tsa rona di na le tumelano ya mojaki le thuso ya selegae.",
                "Re dirisa fela didiriswa tsa letsatsi tse di netefaditsweng go tloga mebaleng e e tsebahalang.",
                "Tshwanelwa: tuelo ya setso, didiriswa tsa boleng, le thuso e e tswang.",
                "Tharollo ya letsatsi e e boleng e simolola ka didiriswa tsa boleng le tuelo ya setso."
            ]
        },

        brands: {
            patterns: ['brands', 'jinko', 'deye', 'victron', 'fronius', 'longi', 'dyness'],
            responses: [
                "We stock leading solar brands including Jinko, Deye, Victron, Fronius, and Longi.",
                "Premium solar brands available - which brand interests you?",
                "Quality solar brands to choose from. Any specific brand in mind?",
                "Wide range of trusted solar equipment brands and models available."
            ]
        },

        brands_tn: {
            patterns: ['mebala', 'marka', 'jinko', 'deye', 'victron', 'fronius', 'longi', 'dyness'],
            responses: [
                "Re na le mebala e e hantileng ya letsatsi go akaretsa Jinko, Deye, Victron, Fronius, le Longi.",
                "Mebala e e tshwanelwang ya letsatsi e gona - e ebileng e bua kae?",
                "Mebala ya letsatsi ya boleng go tloga go yona. Mooki o mongwe?",
                "Bokgoni jwa mebala e e tsebahaleng ya didiriswa tsa letsatsi."
            ]
        },

        solar_panels: {
            patterns: ['solar panels', 'panels', 'photovoltaic', 'pv', 'solar modules'],
            responses: [
                "We offer high-efficiency solar panels from Jinko, Longi, and other premium brands.",
                "Various panel types available - monocrystalline and polycrystalline options.",
                "Solar panels with 20-25 year warranties and high efficiency ratings.",
                "Wide selection of solar panels for residential and commercial use."
            ]
        },

        solar_panels_tn: {
            patterns: ['diphanele tsa letsatsi', 'panels', 'photovoltaic', 'pv'],
            responses: [
                "Re bontsha diphanele tsa letsatsi tsa botlhano go tloga Jinko, Longi, le mebala e mengwe.",
                "Dipalo tsa diphanele di gona - monocrystalline le polycrystalline.",
                "Diphanele tsa letsatsi tse di nang le tumelano ya dingwaga tse 20-25.",
                "Bokgoni jwa diphanele tsa letsatsi bakeng sa residential le commercial."
            ]
        },

        batteries: {
            patterns: ['batteries', 'battery', 'lithium', 'storage', 'energy storage'],
            responses: [
                "We offer lithium batteries from top brands like Dyness, Freedom Won, and Victron.",
                "Battery storage solutions for backup power and solar energy storage.",
                "Long-lasting lithium batteries with 5-10 year warranties available.",
                "From small home batteries to large commercial storage systems."
            ]
        },

        batteries_tn: {
            patterns: ['batiri', 'boloko', 'enerji'],
            responses: [
                "Re bontsha batiri tsa lithium go tloga mebaleng e e hantileng jaaka Dyness, Freedom Won, le Victron.",
                "Tharollo ya boloko ya batiri bakeng sa manno le enerji ya letsatsi.",
                "Batiri tsa lithium tse di nang le tumelano ya dingwaga tse 5-10.",
                "Go tloga ka batiri ba lapeng go ya ka boloko jwa dijobo."
            ]
        },

        hours: {
            patterns: ['hours', 'open', 'close', 'time', 'when', 'business hours'],
            responses: [
                "We're open Monday to Friday 8am-6pm, Saturday 9am-4pm.",
                "Business hours: Mon-Fri 8am-6pm, Sat 9am-4pm. WhatsApp 24/7!",
                "Office hours are Mon-Fri 8am-6pm, Saturday 9am-4pm.",
                "We're here during business hours but WhatsApp is 24/7!"
            ]
        },

        hours_tn: {
            patterns: ['nako', 'bula', 'vala', 'diura', 'nako ya kgwebo'],
            responses: [
                "Re bule Manthe - Labobedi 8am-6pm, Mosobologa 9am-4pm.",
                "Nako ya kgwebo: Mo - Mo 8am-6pm, Mo 9am-4pm. WhatsApp ka dinako tsohle!",
                "Nako ya ofisi ke Mo-Mo 8am-6pm, Mosobologa 9am-4pm.",
                "Re gona ka nako ya kgwebo eupo WhatsApp e na le go di dirwa ka dinako tsohle!"
            ]
        },

        maintenance: {
            patterns: ['maintenance', 'service', 'repair', 'support', 'help'],
            responses: [
                "We provide ongoing maintenance and support for all solar systems.",
                "Regular maintenance keeps your solar system running efficiently.",
                "Our team offers troubleshooting and repair services across Botswana.",
                "Need help? Our technical support team is ready to assist you."
            ]
        },

        maintenance_tn: {
            patterns: ['tshireletso', 'ditirelo', 'pheko', 'thus', 'netefatso'],
            responses: [
                "Re fana ka tshireletso e e tswang le thuso bakeng sa meaho yotlwana ya letsatsi.",
                "Tshireletso e e ntseng e tshwanelwa e dirisa sisiteme ya letsatsi ka tshwanelo.",
                "Timakgolo ya rona e fana ka ditirelo tsa pheko le tshireletso mo Botswana.",
                "O batla thuso? Timakgolo ya rona ya thekniki e tshwanelwa go go thusa."
            ]
        },

        default: {
            responses: [
                "I can help with solar panels, inverters, batteries, installation, pricing, and contact info. What do you need?",
                "Ask me about solar products, services, or contact details. What interests you?",
                "I'm here to help with solar energy questions. Try asking about our products or services!",
                "Need solar help? Ask about panels, inverters, batteries, or contact us directly!",
                "I have information about pricing, installation, and solar products. What would you like to know?",
                "From solar panels to financing options, I'm here to assist. What solar topic interests you?",
                "Need guidance on solar energy? Just ask about products, installation, or contact details!",
                "Whether it's about specific solar products, financing, or our services - I'm here to help!"
            ]
        },

        default_tn: {
            responses: [
                "Nka go thusa ka diphanele tsa letsatsi, inverters, batiri, tuelo, theko, le info ya dithaelano. O batla eng?",
                "Botso ka didiriswa tsa letsatsi, ditirelo, goba info ya dithaelano. O ratang eng?",
                "Ke gona go go thusa ka dipotso tsa enerji ya letsatsi. Botsa ka didiriswa tsa rona!",
                "O batla thuso ya letsatsi? Botsa ka diphanele, inverters, batiri, goba itsaea rona ka kotlololo!",
                "Ke na le info ya theko, tuelo, le didiriswa tsa letsatsi. O batla go itse eng?",
                "Go tloga ka diphanele tsa letsatsi go ya ka dithuso tsa dituelo, ke gona go go thusa.",
                "O batla tataiso ya enerji ya letsatsi? Botsa ka didiriswa, tuelo, goba info ya dithaelano!",
                "Ee e bileng ka didiriswa tsa letsatsi, dithuso tsa dituelo, goba ditirelo tsa rona - ke gona go go thusa!"
            ]
        },

        insurance: {
            patterns: ['insurance', 'insure', 'coverage', 'policy'],
            responses: [
                "Insurance available through local providers. We can recommend partners.",
                "Car insurance options available. Contact us for provider recommendations.",
                "Get insurance quotes from our partner network. Ask us how!",
                "Insurance help available. We'll connect you with trusted providers."
            ]
        },

        test_drive: {
            patterns: ['test drive', 'drive', 'test', 'try car'],
            responses: [
                "Test drives available! Contact the seller directly to arrange.",
                "Schedule a test drive with the seller before buying.",
                "Always test drive before purchasing. Contact sellers to arrange!",
                "Test drives are essential. Reach out to sellers to set one up."
            ]
        },

        warranty: {
            patterns: ['warranty', 'guarantee', 'coverage', 'protection'],
            responses: [
                "Warranty terms vary by seller and vehicle age. Ask sellers directly.",
                "Check with individual sellers about warranty options available.",
                "Warranty information depends on the car and seller. Contact them!",
                "No standard warranty - each seller has different terms."
            ]
        },

        delivery: {
            patterns: ['delivery', 'shipping', 'transport', 'bring car'],
            responses: [
                "Delivery options depend on the seller. Contact them directly.",
                "Some sellers offer delivery services - ask when you contact them.",
                "Delivery arrangements are made directly with sellers.",
                "Ask sellers about delivery options when you contact them."
            ]
        },

        documentation: {
            patterns: ['documents', 'paperwork', 'registration', 'license', 'papers'],
            responses: [
                "You'll need driver's license, proof of income, and insurance documents.",
                "Required documents: ID, driver's license, proof of residence.",
                "Bring your driver's license and proof of income for financing.",
                "Standard documents: ID, driver's license, and proof of address."
            ]
        },

        payment: {
            patterns: ['payment', 'pay', 'cash', 'card', 'bank transfer', 'methods'],
            responses: [
                "We accept cash, bank transfers, and card payments.",
                "Payment methods: cash, bank transfer, or card payments accepted.",
                "Various payment options available - discuss with sellers.",
                "Flexible payment methods - cash, card, or bank transfer."
            ]
        }
    }

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    }

    useEffect(() => {
        scrollToBottom()
    }, [messages])

    // Typewriter effect for bot messages
    useEffect(() => {
        if (typingMessageId !== null) {
            const message = messages.find(m => m.id === typingMessageId)
            if (message && displayedText.length < message.text.length) {
                const timeout = setTimeout(() => {
                    setDisplayedText(message.text.slice(0, displayedText.length + 1))
                }, 30) // Typing speed: 30ms per character
                return () => clearTimeout(timeout)
            } else if (message && displayedText.length === message.text.length) {
                // Typing complete
                setTypingMessageId(null)
                setDisplayedText('')
            }
        }
    }, [typingMessageId, displayedText, messages])


    const getResponse = (userMessage) => {
        const message = userMessage.toLowerCase().trim()
        
        // Check for language switching
        if (message.includes('setswana') || message.includes('tswana') || message.includes('botswana language') || message.includes('translate')) {
            if (language === 'en') {
                return { response: "Click the 🇧🇼 flag button to switch to Setswana! O ka tlosa faele ya Setswana.", isDefault: false }
            } else {
                return { response: "Ke e dirile! O kana wa Setswana jaana. Click the 🇬🇧 flag to switch back to English.", isDefault: false }
            }
        }
        
        // Find matching knowledge base entry (prioritize language-specific responses)
        for (const [key, value] of Object.entries(knowledgeBase)) {
            if (value.patterns) {
                const matched = value.patterns.some(pattern => 
                    message.includes(pattern.toLowerCase())
                )
                if (matched) {
                    // Get random response from the responses array
                    const responses = value.responses
                    const randomResponse = responses[Math.floor(Math.random() * responses.length)]
                    return { response: randomResponse, isDefault: false }
                }
            }
        }
        
        // Get random default response based on language
        const defaultResponses = language === 'en' ? knowledgeBase.default.responses : knowledgeBase.default_tn.responses
        const randomDefaultResponse = defaultResponses[Math.floor(Math.random() * defaultResponses.length)]
        return { response: randomDefaultResponse, isDefault: true }
    }


    const handleSendMessage = async (e) => {
        e.preventDefault()
        if (!inputMessage.trim()) return

        const userMessage = {
            id: messages.length + 1,
            text: inputMessage,
            sender: 'user',
            timestamp: new Date()
        }

        setMessages(prev => [...prev, userMessage])
        setInputMessage('')
        setIsTyping(true)




        // Check for trigger words that indicate user wants human assistance
        const triggerWords = ['agent', 'live', 'person', 'human', 'further assistance', 'real person', 'speak to someone', 'talk to agent', 'customer service', 'representative', 'support', 'help desk', 'staff', 'need human', 'need help', 'talk to person', 'real agent', 'chat with person', 'connect to human', 'escalate', 'supervisor', 'manager', 'a human', 'speak to human', 'talk to human', 'connect to agent', 'real person', 'actual person']
        const hasTriggerWord = triggerWords.some(word => inputMessage.toLowerCase().includes(word))

        // Simulate typing delay, then start typewriter effect
        setTimeout(() => {
            let response = getResponse(inputMessage)
            


            // If user asks for human, give a specific response and trigger WhatsApp
            if (hasTriggerWord) {
                response = {
                    response: "I completely understand! 😊 Let me connect you with one of our human agents right away. They can provide you with personalized assistance for your solar energy inquiry.",
                    isDefault: false,
                    needsEscalation: true
                }
                setFailedAnswers(3) // Force trigger WhatsApp button
                setShowEscalation(true) // Immediate trigger for escalation
            }
            
            // Create the bot message with empty text initially (will be typed)
            const newMessageId = messages.length + 2
            const botResponse = {
                id: newMessageId,
                text: response.response,
                sender: 'bot',
                timestamp: new Date()
            }
            setMessages(prev => [...prev, botResponse])
            
            // Start typewriter effect
            setTypingMessageId(newMessageId)
            setDisplayedText('')
            
            // Track failed answers for WhatsApp button (only if not already triggered and not a trigger word)
            if (response.isDefault && !hasTriggerWord) {
                setFailedAnswers(prev => prev + 1)
            }
            
            // Hide quick actions after first real interaction
            if (showQuickActions) {
                setShowQuickActions(false)
            }
            
            setIsTyping(false)
        }, 1000)
    }





    const handleClearChat = () => {
        setMessages([
            {
                id: 1,
                text: language === 'en' 
                    ? "Hi! Welcome to KingSolarman. How can I help you with solar energy solutions today?"
                    : "Dumela! O amogeleswa ke KingSolarman. Nka thusa jang ka didiriswa tsa letsatsi?",
                sender: 'bot',
                timestamp: new Date()
            }
        ])
        setFailedAnswers(0)
        setShowEscalation(false)
        setShowQuickActions(true)
        setInputMessage('')
    }



    const handleQuickAction = (action) => {
        const quickActions = {
            browse_cars: language === 'en' 
                ? "I want to browse solar products" 
                : "Ke batla go lebelela didiriswa tsa letsatsi",
            sell_car: language === 'en' 
                ? "I need solar installation services" 
                : "Ke batla ditirelo tsa tuelo ya letsatsi", 
            financing: language === 'en' 
                ? "Tell me about solar financing" 
                : "Mpolelele ka dithuso tsa dituelo",
            contact: language === 'en' 
                ? "How can I contact you?" 
                : "Nka lalela jang?"
        }
        
        const message = quickActions[action] || ""
        if (message) {
            setShowQuickActions(false)
            
            // Use functional updates to get the latest messages state
            setMessages(prevMessages => {
                const userMessageId = prevMessages.length + 1
                const userMessage = {
                    id: userMessageId,
                    text: message,
                    sender: 'user',
                    timestamp: new Date()
                }
                
                // Trigger bot response after user message is added
                setIsTyping(true)
                
                setTimeout(() => {
                    const response = getResponse(message)
                    const botMessageId = userMessageId + 1
                    const botResponse = {
                        id: botMessageId,
                        text: response.response,
                        sender: 'bot',
                        timestamp: new Date()
                    }
                    setMessages(prev => [...prev, botResponse])
                    
                    // Start typewriter effect for the bot response
                    setTypingMessageId(botMessageId)
                    setDisplayedText('')
                    
                    setIsTyping(false)
                }, 1000)
                
                return [...prevMessages, userMessage]
            })
        }
    }


    const handleWhatsAppContact = () => {
        const phoneNumber = '+26771548257'
        const message = "Hello, I need assistance with my solar energy inquiry. Perhaps you would like to talk to one of your agents for this matter."
        const encodedMessage = encodeURIComponent(message)
        const whatsappUrl = `https://wa.me/${phoneNumber.replace('+', '')}?text=${encodedMessage}`
        window.open(whatsappUrl, '_blank')
    }



    const WhatsAppButton = () => {
        // Show WhatsApp button if user asked for human OR had 2+ failed answers
        if (failedAnswers >= 2 || showEscalation) {
            return (
                <div className="whatsapp-support-section">

                    <div className="whatsapp-message">
                        <p style={{alignItems: 'center'}} >
                            {language === 'en' ? 'Perhaps you can chat with our agent' : 'Ka boikopanyo, buisa le mothusaši wa rona'}</p>
                    </div>
                    <button 
                        className="whatsapp-agent-button"
                        onClick={handleWhatsAppContact}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893c0-3.18-1.24-6.169-3.495-8.424" />
                        </svg>
                        {language === 'en' ? 'Chat with an Agent' : 'Buisa le Mothusaši'}
                    </button>
                </div>
            )
        }
        return null
    }

    return (
        <>
            {/* Chatbot Container */}
            <div className={`chatbot-container ${isOpen ? 'open' : 'closed'}`}>
                {/* Chat Toggle Button */}
                <button 
                    className="chatbot-toggle"
                    onClick={() => setIsOpen(!isOpen)}
                    aria-label="Toggle chatbot"
                >
                    {isOpen ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                    ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
                        </svg>
                    )}
                </button>

                {/* Chat Window */}
                {isOpen && (
                    <div className="chatbot-window">
                        {/* Header */}
                        <div className="chatbot-header">
                            <div className="chatbot-avatar">
                                <img src="./logo.png" alt="KingSolarman" />
                            </div>
                            <div className="chatbot-info">
                                <h3 style={{color:'#ffffff'}}>
                                    KingSolarman Assistant
                                </h3>
                                <span className="status">{language === 'en' ? '🟢 Online - Ready to chat!' : '🟢 K готов go bua!'}</span>
                            </div>
                            
                            {/* Language Toggle */}
                            <div className="language-toggle">
                                <button 
                                    className={`lang-btn ${language === 'en' ? 'active' : ''}`}
                                    onClick={() => setLanguage('en')}
                                    title="English"
                                >
                                    🇬🇧
                                </button>
                                <button 
                                    className={`lang-btn ${language === 'tn' ? 'active' : ''}`}
                                    onClick={() => setLanguage('tn')}
                                    title="Setswana"
                                >
                                    🇧🇼
                                </button>
                            </div>
                            
                            <button 
                                className="clear-chat-button"
                                onClick={handleClearChat}
                                title="Clear chat"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                                </svg>
                            </button>
                        </div>

                        {/* Messages */}
                        <div className="chatbot-messages">
                            {messages.map((message) => (
                                <div key={message.id} className={`message ${message.sender}`}>
                                    <div className="message-content">
                                        <div className="message-text">
                                            {message.sender === 'bot' && typingMessageId === message.id ? (
                                                displayedText.split('\n').map((line, index) => (
                                                    <p key={index}>{line}<span className="typewriter-cursor">|</span></p>
                                                ))
                                            ) : (
                                                message.text.split('\n').map((line, index) => (
                                                    <p key={index}>{line}</p>
                                                ))
                                            )}
                                        </div>
                                        <span className="message-time">
                                            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </div>
                                </div>
                            ))}

                            {isTyping && (
                                <div className="message bot">
                                    <div className="message-content">
                                        <div className="typing-indicator">
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                        </div>
                                        <span className="typing-text">Assistant is typing...</span>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>


                        {/* WhatsApp Support Section */}
                        <WhatsAppButton />

                        {/* Quick Action Buttons */}
                        {showQuickActions && messages.length === 1 && (
                            <div className="quick-actions-section">
                                <div className="quick-actions-title">
                                    <p>{language === 'en' ? 'Quick actions to get started:' : 'Ditheko tsa botlhano go thoma:'}</p>
                                </div>

                                <div className="quick-actions-buttons">

                                    <button 
                                        className="quick-action-btn browse"
                                        onClick={() => handleQuickAction('browse_cars')}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                                        </svg>
                                        {language === 'en' ? 'Browse Products' : 'Bontsha Dithoto'}
                                    </button>
                                    <button 
                                        className="quick-action-btn install"
                                        onClick={() => handleQuickAction('sell_car')}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                        </svg>
                                        {language === 'en' ? 'Installation Services' : 'Ditirelo tsa Tuelo'}
                                    </button>
                                    <button 
                                        className="quick-action-btn finance"
                                        onClick={() => handleQuickAction('financing')}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
                                        </svg>
                                        {language === 'en' ? 'Solar Financing' : 'Dithuso tsa Dituelo'}
                                    </button>
                                    <button 
                                        className="quick-action-btn contact"
                                        onClick={() => handleQuickAction('contact')}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                                        </svg>
                                        {language === 'en' ? 'Contact Info' : 'Info ya Dithaelano'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Input Form */}
                        <form onSubmit={handleSendMessage} className="chatbot-input">
                            <input
                                type="text"
                                value={inputMessage}
                                onChange={(e) => setInputMessage(e.target.value)}
                                placeholder="Type your message..."
                                className="message-input"
                            />
                            <button type="submit" className="send-button">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                                </svg>
                            </button>
                        </form>
                    </div>
                )}
            </div>


            <style jsx>{`
                /* Global mobile optimizations */
                .chatbot-container {
                    position: fixed;
                    bottom: 20px;
                    right: 20px;
                    z-index: 1000;
                    font-family: 'Inter', sans-serif;
                    /* Prevent zoom on mobile */
                    touch-action: manipulation;
                    -webkit-touch-callout: none;
                    -webkit-user-select: none;
                    user-select: none;
                }

                /* iOS Safari specific fixes */
                @supports (-webkit-touch-callout: none) {
                    .chatbot-container {
                        /* Fix for iOS Safari viewport issues */
                        position: -webkit-fixed;
                        bottom: 80px;
                    }
                    
                    .chatbot-window {
                        /* iOS Safari specific */
                        -webkit-overflow-scrolling: touch;
                    }
                    
                    .message-input {
                        /* iOS Safari input zoom prevention */
                        font-size: 16px !important;
                        transform: translateZ(0);
                    }
                }

                .chatbot-toggle {
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #2E5C3A 0%, #D4AF37 100%);
                    border: none;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    box-shadow: 0 4px 20px rgba(46, 92, 58, 0.4);
                    transition: all 0.3s ease;
                    position: relative;
                }

                .chatbot-toggle:hover {
                    transform: scale(1.1);
                    box-shadow: 0 6px 25px rgba(46, 92, 58, 0.6);
                }

                .chatbot-window {
                    position: absolute;
                    bottom: 80px;
                    right: 0;
                    width: 380px;
                    height: 500px;
                    background: white;
                    border-radius: 20px;
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    animation: slideUp 0.3s ease-out;
                }

                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .chatbot-header {
                    background: linear-gradient(135deg, #2E5C3A 0%, #D4AF37 100%);
                    color: white;
                    padding: 15px 20px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    position: relative;
                }

                .chatbot-avatar {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    overflow: hidden;
                    background: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .chatbot-avatar img {
                    width: 35px;
                    height: 35px;
                    object-fit: contain;
                }

                .chatbot-info {
                    flex: 1;
                }


                .chatbot-info h3 {
                    margin: 0;
                    font-size: 16px;
                    font-weight: 600;
                    color: #ffffff !important;
                }

                .status {
                    font-size: 12px;
                    opacity: 0.8;
                }

                .language-toggle {
                    display: flex;
                    gap: 4px;
                    padding: 4px;
                    background: rgba(255, 255, 255, 0.2);
                    border-radius: 20px;
                }

                .lang-btn {
                    width: 28px;
                    height: 28px;
                    border: none;
                    border-radius: 50%;
                    background: transparent;
                    cursor: pointer;
                    font-size: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    opacity: 0.6;
                }

                .lang-btn:hover {
                    opacity: 1;
                    background: rgba(255, 255, 255, 0.1);
                }

                .lang-btn.active {
                    opacity: 1;
                    background: white;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
                }

                .clear-chat-button {
                    background: rgba(255, 255, 255, 0.2);
                    border: none;
                    color: white;
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                }

                .clear-chat-button:hover {
                    background: rgba(255, 255, 255, 0.3);
                    transform: scale(1.05);
                }

                .chatbot-messages {
                    flex: 1;
                    padding: 20px;
                    overflow-y: auto;
                    background: #f8f9fa;
                }

                .message {
                    margin-bottom: 15px;
                    display: flex;
                }

                .message.user {
                    justify-content: flex-end;
                }

                .message.bot {
                    justify-content: flex-start;
                }

                .message-content {
                    max-width: 80%;
                    position: relative;
                }

                .message.user .message-content {
                    background: #2E5C3A;
                    color: white;
                    padding: 12px 16px;
                    border-radius: 18px 18px 4px 18px;
                }

                .message.bot .message-content {
                    background: #F5F0E6;
                    color: #3C2B1E;
                    padding: 12px 16px;
                    border-radius: 18px 18px 18px 4px;
                    box-shadow: 0 2px 8px rgba(46, 92, 58, 0.1);
                }

                .message-text p {
                    margin: 0;
                    line-height: 1.4;
                    font-size: 14px;
                }

                .typewriter-cursor {
                    display: inline-block;
                    width: 2px;
                    height: 1em;
                    background-color: #2E5C3A;
                    margin-left: 2px;
                    animation: blink 1s step-end infinite;
                    vertical-align: text-bottom;
                }

                @keyframes blink {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0; }
                }

                .message-time {
                    font-size: 11px;
                    opacity: 0.7;
                    margin-top: 5px;
                    display: block;
                }

                .typing-indicator {
                    display: flex;
                    gap: 4px;
                    padding: 12px 0;
                }

                .typing-indicator span {
                    width: 8px;
                    height: 8px;
                    background: #2E5C3A;
                    border-radius: 50%;
                    animation: typing 1.4s infinite ease-in-out;
                }

                .typing-indicator span:nth-child(2) {
                    animation-delay: 0.2s;
                }


                .typing-indicator span:nth-child(3) {
                    animation-delay: 0.4s;
                }

                .typing-text {
                    font-size: 12px;
                    color: #5A4A3C;
                    margin-top: 8px;
                    font-style: italic;
                    display: block;
                }

                @keyframes typing {
                    0%, 60%, 100% {
                        transform: translateY(0);
                        opacity: 0.5;
                    }
                    30% {
                        transform: translateY(-10px);
                        opacity: 1;
                    }
                }

                .whatsapp-support-section {
                    padding: 15px 20px;
                    background: #f8f9fa;
                    border-top: 1px solid #eee;
                    text-align: center;
                }

                .whatsapp-message {
                    margin-bottom: 12px;
                }

                .whatsapp-message p {
                    margin: 0;
                    font-size: 14px;
                    color: #2E5C3A;
                    font-weight: 500;
                }

                .whatsapp-agent-button {
                    background: #D4AF37;
                    border: none;
                    color: white;
                    padding: 10px 20px;
                    border-radius: 25px;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin: 0 auto;
                    transition: all 0.3s ease;
                }


                .whatsapp-agent-button:hover {
                    background: #B8941F;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(212, 175, 55, 0.4);
                }

                /* Quick Actions Styling */
                .quick-actions-section {
                    padding: 15px 20px;
                    background: #f8f9fa;
                    border-top: 1px solid #eee;
                }

                .quick-actions-title {
                    margin-bottom: 12px;
                    text-align: center;
                }

                .quick-actions-title p {
                    margin: 0;
                    font-size: 14px;
                    color: #2E5C3A;
                    font-weight: 500;
                }

                .quick-actions-buttons {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 8px;
                }

                .quick-action-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px 16px;
                    border: none;
                    border-radius: 12px;
                    font-size: 13px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    text-align: center;
                    justify-content: center;
                    min-height: 44px;
                }

                .quick-action-btn.browse {
                    background: rgba(46, 92, 58, 0.1);
                    color: #2E5C3A;
                    border: 1px solid rgba(46, 92, 58, 0.2);
                }

                .quick-action-btn.install {
                    background: rgba(212, 175, 55, 0.1);
                    color: #D4AF37;
                    border: 1px solid rgba(212, 175, 55, 0.2);
                }

                .quick-action-btn.finance {
                    background: rgba(46, 92, 58, 0.1);
                    color: #2E5C3A;
                    border: 1px solid rgba(46, 92, 58, 0.2);
                }

                .quick-action-btn.contact {
                    background: rgba(212, 175, 55, 0.1);
                    color: #D4AF37;
                    border: 1px solid rgba(212, 175, 55, 0.2);
                }

                .quick-action-btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 2px 8px rgba(46, 92, 58, 0.2);
                }

                .quick-action-btn:active {
                    transform: scale(0.98);
                }

                .quick-action-btn svg {
                    flex-shrink: 0;
                }

                .chatbot-input {
                    display: flex;
                    padding: 15px 20px;
                    background: white;
                    border-top: 1px solid rgba(46, 92, 58, 0.1);
                    gap: 10px;
                }

                .message-input {
                    flex: 1;
                    border: 2px solid rgba(46, 92, 58, 0.2);
                    border-radius: 20px;
                    padding: 10px 15px;
                    font-size: 14px;
                    outline: none;
                    transition: border-color 0.2s ease;
                    background: #F5F0E6;
                    color: #3C2B1E;
                }

                .message-input:focus {
                    border-color: #2E5C3A;
                    box-shadow: 0 0 0 3px rgba(46, 92, 58, 0.1);
                }

                .send-button {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background: #2E5C3A;
                    border: none;
                    color: white;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                }

                .send-button:hover {
                    background: #1E462A;
                    transform: scale(1.05);
                }



                /* Mobile Responsive */
                @media (max-width: 480px) {
                    .chatbot-container {
                        right: 10px;
                        bottom: 10px;
                        z-index: 9999;
                    }

                    .chatbot-toggle {
                        width: 56px;
                        height: 56px;
                        bottom: 70px;
                        right: 15px;
                        /* Better touch feedback */
                        user-select: none;
                        -webkit-tap-highlight-color: transparent;
                    }

                    .chatbot-window {
                        width: calc(100vw - 20px);
                        height: 80vh;
                        max-height: 600px;
                        bottom: 80px;
                        right: 10px;
                        border-radius: 15px;
                        animation: slideUpMobile 0.3s ease-out;
                        /* Prevent zoom on input focus */
                        transform: translateZ(0);
                        -webkit-transform: translateZ(0);
                    }

                    @keyframes slideUpMobile {
                        from {
                            opacity: 0;
                            transform: translateY(30px) scale(0.95);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0) scale(1);
                        }
                    }

                    .chatbot-header {
                        padding: 12px 15px;
                        border-radius: 15px 15px 0 0;
                        /* Prevent text selection on mobile */
                        -webkit-user-select: none;
                        -moz-user-select: none;
                        -ms-user-select: none;
                        user-select: none;
                    }

                    .chatbot-avatar {
                        width: 36px;
                        height: 36px;
                        flex-shrink: 0;
                    }

                    .chatbot-avatar img {
                        width: 30px;
                        height: 30px;
                    }

                    .chatbot-info h3 {
                        font-size: 15px;
                        font-weight: 600;
                    }

                    .status {
                        font-size: 11px;
                        opacity: 0.9;
                    }

                    .clear-chat-button {
                        width: 32px;
                        height: 32px;
                        /* Better touch targets */
                        min-width: 32px;
                        min-height: 32px;
                    }

                    .chatbot-messages {
                        padding: 15px;
                        height: calc(80vh - 160px);
                        /* Smooth scrolling */
                        -webkit-overflow-scrolling: touch;
                        scroll-behavior: smooth;
                    }

                    .message {
                        margin-bottom: 12px;
                    }

                    .message-content {
                        max-width: 85%;
                        /* Prevent text overflow */
                        word-wrap: break-word;
                        overflow-wrap: break-word;
                    }

                    .message.user .message-content {
                        padding: 10px 14px;
                        border-radius: 16px 16px 4px 16px;
                        /* Better touch feedback */
                        -webkit-tap-highlight-color: rgba(255, 255, 255, 0.1);
                    }

                    .message.bot .message-content {
                        padding: 10px 14px;
                        border-radius: 16px 16px 16px 4px;
                        /* Better touch feedback */
                        -webkit-tap-highlight-color: rgba(0, 0, 0, 0.05);
                    }

                    .message-text p {
                        font-size: 13px;
                        line-height: 1.3;
                        /* Prevent text selection */
                        -webkit-user-select: none;
                        -moz-user-select: none;
                        -ms-user-select: none;
                        user-select: none;
                    }

                    .message-time {
                        font-size: 10px;
                        opacity: 0.7;
                    }

                    .typing-indicator {
                        padding: 8px 0;
                    }

                    .typing-indicator span {
                        width: 6px;
                        height: 6px;
                    }


                    .whatsapp-support-section {
                        padding: 12px 15px;
                        /* Better touch area */
                        min-height: 60px;
                    }

                    .quick-actions-section {
                        padding: 12px 15px;
                        /* Better touch area */
                        min-height: 80px;
                    }

                    .quick-actions-buttons {
                        grid-template-columns: 1fr 1fr;
                        gap: 6px;
                    }

                    .quick-action-btn {
                        padding: 10px 12px;
                        font-size: 12px;
                        min-height: 42px;
                        /* Better touch feedback */
                        -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
                    }

                    .quick-action-btn svg {
                        width: 14px;
                        height: 14px;
                    }

                    .whatsapp-message {
                        margin-bottom: 8px;
                    }

                    .whatsapp-message p {
                        font-size: 13px;
                        font-weight: 500;
                    }

                    .whatsapp-agent-button {
                        padding: 12px 16px;
                        font-size: 13px;
                        font-weight: 600;
                        border-radius: 20px;
                        /* Better touch targets */
                        min-height: 44px;
                        min-width: 120px;
                        /* Better touch feedback */
                        -webkit-tap-highlight-color: rgba(37, 211, 102, 0.2);
                    }

                    .chatbot-input {
                        padding: 12px 15px;
                        gap: 8px;
                        border-radius: 0 0 15px 15px;
                        /* Better touch scrolling */
                        -webkit-overflow-scrolling: touch;
                    }

                    .message-input {
                        padding: 12px 16px;
                        font-size: 16px; /* Prevents zoom on iOS */
                        border-radius: 18px;
                        /* Better mobile input handling */
                        -webkit-appearance: none;
                        appearance: none;
                        /* Better touch feedback */
                        -webkit-tap-highlight-color: rgba(107, 183, 233, 0.1);
                    }

                    .send-button {
                        width: 44px;
                        height: 44px;
                        min-width: 44px;
                        min-height: 44px;
                        /* Better touch feedback */
                        -webkit-tap-highlight-color: rgba(107, 183, 233, 0.2);
                    }
                }

                /* Extra small screens */
                @media (max-width: 360px) {
                    .chatbot-window {
                        width: calc(100vw - 10px);
                        right: 5px;
                        height: 85vh;
                    }

                    .chatbot-messages {
                        height: calc(85vh - 160px);
                    }

                    .chatbot-header {
                        padding: 10px 12px;
                    }

                    .chatbot-input {
                        padding: 10px 12px;
                    }

                    .message-input {
                        padding: 10px 14px;
                    }
                }

                /* Touch device optimizations */
                @media (hover: none) and (pointer: coarse) {
                    .chatbot-toggle:hover {
                        transform: none;
                        box-shadow: 0 4px 20px rgba(107, 183, 233, 0.4);
                    }

                    .chatbot-toggle:active {
                        transform: scale(0.95);
                        transition: transform 0.1s ease;
                    }

                    .send-button:hover {
                        background: #6BB7E9;
                        transform: none;
                    }

                    .send-button:active {
                        transform: scale(0.95);
                        background: #1E462A;
                    }

                    .whatsapp-agent-button:hover {
                        background: #D4AF37;
                        transform: none;
                    }

                    .whatsapp-agent-button:active {
                        transform: scale(0.95);
                        background: #B8941F;
                    }
                }

                /* Landscape mobile optimization */
                @media (max-height: 500px) and (orientation: landscape) {
                    .chatbot-window {
                        height: 90vh;
                        width: 400px;
                    }

                    .chatbot-messages {
                        height: calc(90vh - 140px);
                    }
                }

                /* Tablet optimization */
                @media (min-width: 481px) and (max-width: 768px) {
                    .chatbot-window {
                        width: 360px;
                        height: 480px;
                    }

                    .chatbot-container {
                        right: 15px;
                        bottom: 15px;
                    }
                }

                /* Scrollbar Styling */
                .chatbot-messages::-webkit-scrollbar {
                    width: 4px;
                }

                .chatbot-messages::-webkit-scrollbar-track {
                    background: #f1f1f1;
                }

                .chatbot-messages::-webkit-scrollbar-thumb {
                    background: #2E5C3A;
                    border-radius: 2px;
                }
            `}</style>
        </>
    )
}
